import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ClipboardPlus, ListPlus } from "lucide-react";
import NotFound from "@/pages/NotFound";
import AccessDenied from "@/pages/AccessDenied";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { errMsg } from "../../lib/portfolio";
import { FIELD_ROLES, usePhaseFeature } from "../../lib/phaseFeatures";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent } from "../../components/ui/card";
import { SafetyPanel } from "./SafetyPanel";
import { PhotoPanel, CompliancePanel } from "./PhotoCompliancePanels";

export function FieldGate({ children }) {
  const { user } = useAuth();
  const f = usePhaseFeature(4, user?.id);
  if (!f) return <div className="p-8 text-sm font-nav text-erf-dark/70" data-testid="phase4-gate-loading">Loading…</div>;
  if (!FIELD_ROLES.includes(user?.role_key)) return <AccessDenied />; // B2a: role check first → 403 page (no module-state leak)
  if (!f.enabled) return <NotFound />;
  return children;
}

const today = () => new Date().toISOString().slice(0, 10);
const Lbl = ({ label, children, className = "" }) => <label className={`text-sm font-nav text-erf-dark ${className}`}>{label}{children}</label>;

function NewLogForm({ pid, onCreated }) {
  const [f, setF] = useState({ log_date: today(), weather_summary: "", temperature_f: "", trade: "", headcount: "", hours: "", work_performed: "", delays: "" });
  const [error, setError] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    const body = { log_date: f.log_date, weather_summary: f.weather_summary, work_performed: f.work_performed, delays: f.delays,
      manpower: f.trade ? [{ trade: f.trade, headcount: Number(f.headcount || 0), hours: Number(f.hours || 0) }] : [] };
    if (f.temperature_f !== "") body.temperature_f = Number(f.temperature_f);
    api.post(`/phase4/projects/${pid}/daily-logs`, body)
      .then(() => { setError(null); setF({ ...f, work_performed: "", delays: "" }); onCreated(); })
      .catch((err) => setError(errMsg(err, "Could not save the daily report.")));
  };
  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="phase4-log-form">
      <Lbl label="Date"><Input type="date" required value={f.log_date} max={today()} onChange={set("log_date")} className="mt-1 h-11" data-testid="phase4-log-date" /></Lbl>
      <Lbl label="Weather (type it in)"><Input value={f.weather_summary} onChange={set("weather_summary")} className="mt-1 h-11" data-testid="phase4-log-weather" /></Lbl>
      <Lbl label="Temperature °F"><Input type="number" value={f.temperature_f} onChange={set("temperature_f")} className="mt-1 h-11" data-testid="phase4-log-temp" /></Lbl>
      <Lbl label="Trade on site"><Input value={f.trade} onChange={set("trade")} placeholder="Framing" className="mt-1 h-11" data-testid="phase4-log-trade" /></Lbl>
      <Lbl label="Workers"><Input type="number" min="0" value={f.headcount} onChange={set("headcount")} className="mt-1 h-11" data-testid="phase4-log-headcount" /></Lbl>
      <Lbl label="Hours (total)"><Input type="number" min="0" step="0.5" value={f.hours} onChange={set("hours")} className="mt-1 h-11" data-testid="phase4-log-hours" /></Lbl>
      <Lbl label="Work performed" className="sm:col-span-2 lg:col-span-4"><Textarea required value={f.work_performed} onChange={set("work_performed")} className="mt-1" data-testid="phase4-log-work" /></Lbl>
      <Lbl label="Delays" className="sm:col-span-2 lg:col-span-4"><Textarea value={f.delays} onChange={set("delays")} className="mt-1" data-testid="phase4-log-delays" /></Lbl>
      <div className="sm:col-span-2 lg:col-span-4">
        <Button type="submit" className="h-11 bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="phase4-log-submit">
          <ClipboardPlus className="w-4 h-4 mr-1.5" aria-hidden="true" />Save draft report
        </Button>
      </div>
      {error && <p role="alert" className="text-sm text-[#991B1B] sm:col-span-2 lg:col-span-4" data-testid="phase4-log-error">{error}</p>}
    </form>
  );
}

function PunchPanel({ pid, canClose, focus }) {
  const [rows, setRows] = useState([]);
  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const load = useCallback(() => api.get(`/phase4/projects/${pid}/punch`).then((r) => setRows(r.data)), [pid]);
  useEffect(() => { load(); }, [load]);
  const run = (p) => p.then(() => { setError(null); load(); }).catch((e) => setError(errMsg(e, "That did not work.")));
  const add = (e) => { e.preventDefault(); run(api.post(`/phase4/projects/${pid}/punch`, { title, location }).then(() => { setTitle(""); setLocation(""); })); };
  return (
    <Card className="ds-card border-erf-borderCream mt-6" data-testid="phase4-punch-panel">
      <CardContent className="p-5">
        <h2 className="font-display font-semibold text-erf-dark text-base md:text-lg mb-3">Punch list</h2>
        <form onSubmit={add} className="grid gap-3 sm:grid-cols-[2fr_1fr_auto] items-end mb-3">
          <Lbl label="Item"><Input required value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 h-11" data-testid="phase4-punch-title" /></Lbl>
          <Lbl label="Location"><Input value={location} onChange={(e) => setLocation(e.target.value)} className="mt-1 h-11" data-testid="phase4-punch-location" /></Lbl>
          <Button type="submit" className="h-11 bg-erf-dark text-[#FEFCF7]" data-testid="phase4-punch-add"><ListPlus className="w-4 h-4 mr-1.5" aria-hidden="true" />Add item</Button>
        </form>
        {canClose && <Lbl label="Reason for reopening (at least 10 characters)" className="block mb-3"><Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 h-11" data-testid="phase4-punch-reason" /></Lbl>}
        {error && <p role="alert" className="mb-2 text-sm text-[#991B1B]" data-testid="phase4-punch-error">{error}</p>}
        <ul className="space-y-2">
          {rows.length === 0 && <li className="text-sm font-nav text-erf-dark/80">No punch items yet.</li>}
          {rows.map((r) => (
            <li key={r.id} ref={focus === r.id ? (el) => el && el.scrollIntoView({ block: "center" }) : undefined}
              className={`flex flex-wrap items-center gap-2 text-sm font-nav text-erf-dark ${focus === r.id ? "rounded-md ring-2 ring-erf-gold p-1" : ""}`}
              data-testid={`phase4-punch-${r.number}`} data-focused={focus === r.id ? "true" : undefined}>
              <strong>{r.number}</strong><span className="mr-auto">{r.title}{r.location ? ` · ${r.location}` : ""} — {r.state}</span>
              {r.state === "open" && <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => run(api.post(`/phase4/punch/${r.id}/ready`))} data-testid={`phase4-punch-ready-${r.number}`}>Ready for inspection</Button>}
              {canClose && r.state === "ready" && <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => run(api.post(`/phase4/punch/${r.id}/close`))} data-testid={`phase4-punch-close-${r.number}`}>Close</Button>}
              {canClose && r.state !== "open" && <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => run(api.post(`/phase4/punch/${r.id}/reopen`, { reason }))} data-testid={`phase4-punch-reopen-${r.number}`}>Reopen</Button>}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export default function FieldWorkspace() {
  const { user } = useAuth();
  const [projects, setProjects] = useState(null);
  const [pid, setPid] = useState(null);
  const [logs, setLogs] = useState([]);
  const [params] = useSearchParams();
  const want = params.get("project");
  useEffect(() => { api.get("/phase4/projects").then((r) => { setProjects(r.data); setPid(r.data.items.find((p) => p.id === want)?.id || r.data.items[0]?.id || null); }); }, [want]);
  const load = useCallback(() => { if (pid) api.get(`/phase4/projects/${pid}/daily-logs`).then((r) => setLogs(r.data)); }, [pid]);
  useEffect(() => { load(); }, [load]);
  const role = projects?.role_key;
  const writer = role && role !== "PROJECT_MANAGER";
  const canClose = ["OWNER_SUPER_ADMIN", "ADMINISTRATOR", "PROJECT_MANAGER"].includes(role);
  return (
    <AdminAppShell activeNav="field" user={user} title="Field Operations" subtitle="Daily reports and punch list for each project."
      breadcrumbs={[{ label: "Owner Command Center", to: "/owner" }, { label: "Field Operations", to: null }]}>
      <div data-testid="phase4-workspace">
        <p className="mb-4 text-sm font-nav text-erf-dark/80" data-testid="phase4-weather-note">Weather is typed in by hand. Automatic weather is waiting on external authorization.</p>
        {projects && projects.items.length === 0 && <p className="text-sm font-nav text-erf-dark" data-testid="phase4-no-projects">No projects yet.</p>}
        <div className="flex flex-wrap gap-2 mb-5" role="group" aria-label="Choose a project" data-testid="phase4-project-picker">
          {(projects?.items || []).map((p) => (
            <Button key={p.id} aria-pressed={pid === p.id} onClick={() => setPid(p.id)} data-testid={`phase4-project-${p.id}`}
              variant={pid === p.id ? "default" : "outline"} className={pid === p.id ? "h-11 bg-erf-dark text-[#FEFCF7]" : "h-11 border-erf-borderCream text-erf-dark bg-white"}>{p.name}</Button>
          ))}
        </div>
        {pid && writer && <Card className="ds-card border-erf-borderCream mb-5"><CardContent className="p-5"><NewLogForm pid={pid} onCreated={load} /></CardContent></Card>}
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="phase4-log-grid">
          {logs.map((l) => (
            <Link key={l.id} to={`/phase4/daily-logs/${l.id}`} data-testid={`phase4-log-card-${l.log_date}`}
              className="ds-card block rounded-xl border border-erf-borderCream bg-[#FEFCF7] p-4 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-erf-gold">
              <div className="font-display font-semibold text-erf-dark">{l.log_date}</div>
              <div className="text-sm font-nav text-erf-dark/80">{l.state} · {l.total_headcount} workers · {l.total_hours} h</div>
            </Link>
          ))}
        </div>
        {pid && <PunchPanel pid={pid} canClose={canClose} focus={params.get("punch")} />}
        {pid && <SafetyPanel key={pid} pid={pid} canClose={canClose} />}
        {pid && <PhotoPanel key={`ph-${pid}`} pid={pid} logs={logs} />}
        {pid && <CompliancePanel key={`c-${pid}`} pid={pid} editor={["OWNER_SUPER_ADMIN", "ADMINISTRATOR", "PROJECT_MANAGER"].includes(role)} />}
      </div>
    </AdminAppShell>
  );
}
